import React, { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, Download } from "lucide-react";

/**
 * Full-screen photo lightbox with keyboard nav (← → Esc) and swipe support.
 * Props:
 * - photos: array of base64 or URL strings
 * - open: boolean
 * - onClose: fn
 * - initialIndex: number (default 0)
 */
export function PhotoLightbox({ photos = [], open, onClose, initialIndex = 0, title }) {
  const [idx, setIdx] = useState(initialIndex);

  useEffect(() => { if (open) setIdx(initialIndex); }, [open, initialIndex]);

  const next = useCallback(() => setIdx((i) => (i + 1) % photos.length), [photos.length]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + photos.length) % photos.length), [photos.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, onClose]);

  if (!open || photos.length === 0) return null;

  const src = photos[idx];

  const download = () => {
    try {
      const a = document.createElement("a");
      a.href = src;
      a.download = `${(title || "foto").replace(/\s+/g, "-").toLowerCase()}-${idx + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {}
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 text-white z-[81]" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm">
          {title && <div className="font-semibold text-secondary">{title}</div>}
          <div className="text-xs opacity-80">Foto {idx + 1} / {photos.length}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={download} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition" aria-label="Unduh" data-testid="btn-lightbox-download">
            <Download className="h-5 w-5" />
          </button>
          <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition" aria-label="Tutup" data-testid="btn-lightbox-close">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Image */}
      <img
        src={src}
        alt={`foto ${idx + 1}`}
        className="max-w-[92vw] max-h-[85vh] object-contain rounded shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid={`lightbox-image-${idx}`}
      />

      {/* Prev/Next */}
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            aria-label="Sebelumnya"
            data-testid="btn-lightbox-prev"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            aria-label="Berikutnya"
            data-testid="btn-lightbox-next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Thumbnails */}
      {photos.length > 1 && photos.length <= 12 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[92vw] overflow-x-auto p-2 bg-black/40 rounded-lg" onClick={(e) => e.stopPropagation()}>
          {photos.map((p, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition ${i === idx ? "border-secondary" : "border-transparent opacity-60 hover:opacity-100"}`}
              data-testid={`lightbox-thumb-${i}`}
            >
              <img src={p} alt={`thumb ${i+1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Hint */}
      <div className="absolute bottom-2 right-4 text-[0.65rem] text-white/50 flex items-center gap-2 pointer-events-none">
        <ZoomIn className="h-3 w-3" /> Esc tutup · ← → navigasi
      </div>
    </div>
  );
}
